import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Fingerprint, Trash2, Loader2, ShieldCheck } from "lucide-react";
import { GlassButton } from "@/components/GlassBits";
import { listPasskeys, registerPasskey, revokePasskey, passkeysSupported, passkeyError }
  from "@/lib/passkeys";

const fecha = (iso) => (iso ? iso.slice(0, 16).replace("T", " ") : "—");

/** Entrar al panel con la huella o la cara de este dispositivo. */
export const PasskeyCard = () => {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setRows(await listPasskeys()); }
    catch (e) { setRows([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    setBusy(true);
    try {
      await registerPasskey();
      toast.success("Dispositivo registrado. Ya puedes entrar con tu huella o cara.");
      load();
    } catch (e) { toast.error(passkeyError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (credentialId) => {
    if (!window.confirm("¿Quitar este dispositivo? Ya no podrá entrar con huella.")) return;
    try {
      await revokePasskey(credentialId);
      toast.success("Dispositivo retirado");
      load();
    } catch (e) { toast.error(passkeyError(e, "No se pudo quitar")); }
  };

  if (!passkeysSupported()) return null;

  return (
    <div className="glass-card p-6 max-w-2xl" data-testid="passkey-card">
      <div className="flex items-center gap-2 mb-2 text-white font-heading font-semibold">
        <Fingerprint className="w-5 h-5 text-red-400" /> Entrar con huella o cara
      </div>
      <p className="text-sm text-white/55 mb-5">
        Registra este teléfono o computadora y entra al panel sin teclear tu código.
        Tu huella nunca sale del dispositivo; aquí solo guardamos la llave pública.
      </p>

      <GlassButton tone="red" icon={ShieldCheck} className="w-auto px-5" loading={busy}
        data-testid="passkey-add-btn" onClick={add}>
        Registrar este dispositivo
      </GlassButton>

      <div className="mt-5 space-y-2" data-testid="passkey-list">
        {rows === null && <Loader2 className="w-5 h-5 animate-spin text-red-400" />}
        {rows?.length === 0 && (
          <p className="text-xs text-white/40" data-testid="passkey-empty">
            Todavía no hay dispositivos registrados.
          </p>
        )}
        {rows?.map((r) => (
          <div key={r.credential_id} data-testid={`passkey-row-${r.credential_id}`}
            className="flex items-center gap-3 px-3.5 py-3 rounded-xl border border-white/10"
            style={{ background: "rgba(255,255,255,0.04)" }}>
            <Fingerprint className="w-4 h-4 text-red-300 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-sm text-white truncate">{r.label || "Passkey"}</div>
              <div className="text-[11px] text-white/40">
                Registrado {fecha(r.created_at)} · Último uso {fecha(r.last_used_at)}
              </div>
            </div>
            <button data-testid={`passkey-remove-${r.credential_id}`} title="Quitar"
              onClick={() => remove(r.credential_id)}
              className="text-white/45 hover:text-red-300 transition-colors p-2">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PasskeyCard;
